import { useState, useMemo } from 'react';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Search, X, Filter } from 'lucide-react';
import { Button } from './ui/button';

/**
 * SearchFilterTable - A reusable table wrapper with search and filter functionality
 * 
 * Props:
 * - data: array of objects to display
 * - columns: array of { key, label, render? } for table headers
 * - searchKeys: array of keys to search in
 * - filters: array of { key, label, options: [{value, label}] } for filter dropdowns
 * - renderRow: function(item, index) to render a table row
 * - emptyMessage: message when no data
 * - searchPlaceholder: placeholder for search input
 */
const SearchFilterTable = ({
  data = [],
  columns = [],
  searchKeys = [],
  filters = [],
  renderRow,
  emptyMessage = 'Geen resultaten gevonden',
  searchPlaceholder = 'Zoeken...',
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  // Filter and search logic
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        searchKeys.some(key => {
          const value = key.split('.').reduce((obj, k) => obj?.[k], item);
          return value?.toString().toLowerCase().includes(query);
        })
      );
    }

    // Apply filters
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        result = result.filter(item => {
          const itemValue = key.split('.').reduce((obj, k) => obj?.[k], item);
          return itemValue === value;
        });
      }
    });

    return result;
  }, [data, searchQuery, activeFilters, searchKeys]);

  const handleFilterChange = (key, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveFilters({});
  };

  const hasActiveFilters = searchQuery || Object.values(activeFilters).some(v => v && v !== 'all');

  return (
    <div className={className}>
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 border-neutral-200 focus:border-black focus:ring-black"
            data-testid="table-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-neutral-100 rounded"
            >
              <X className="h-3 w-3 text-neutral-400" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        {filters.map((filter) => (
          <Select
            key={filter.key}
            value={activeFilters[filter.key] || 'all'}
            onValueChange={(value) => handleFilterChange(filter.key, value)}
          >
            <SelectTrigger className="w-40 border-neutral-200" data-testid={`filter-${filter.key}`}>
              <SelectValue placeholder={filter.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle {filter.label}</SelectItem>
              {filter.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            className="border-neutral-200"
            onClick={clearFilters}
            data-testid="clear-filters-button"
          >
            <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
            Reset
          </Button>
        )}
      </div>

      {/* Results Count */}
      <div className="text-xs text-neutral-500 mb-2">
        {filteredData.length} van {data.length} resultaten
        {hasActiveFilters && ' (gefilterd)'}
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full">
          <thead className="sticky top-0 z-10 bg-white dark:bg-neutral-900">
            <tr className="border-b border-neutral-200 dark:border-neutral-700">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800 ${col.className || ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => renderRow(item, index, filteredData.length))
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center">
                  <Filter className="h-8 w-8 text-neutral-300 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-sm text-neutral-500">{emptyMessage}</p>
                  {hasActiveFilters && (
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2 text-neutral-600"
                      onClick={clearFilters}
                    >
                      Filters wissen
                    </Button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SearchFilterTable;
